import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { isUserLoggedIn, getUserData, logout } from "../../utils/authUtils";
import { clearCart, saveCartItems } from "../../utils/cartUtils";
import { userProfileService } from "../../api/services/userProfileService";
import { orderService } from "../../api/services/orderService";
import defaultProductImage from "../../images/Default-Image.jpeg";
import BillingCardModal from "../BillingCardModal/BillingCardModal";
import OrderHistoryDetailModal from "./OrderHistoryDetailModal";
import "./MyAccount.css";

const SECTIONS = [
  { id: "profile", label: "My Profile" },
  { id: "security", label: "Security" },
  // { id: "payments", label: "Payments" },
  { id: "orders", label: "Order History" },
];

const ORDER_PAGE_SIZE = 6;
const ERP_IMAGE_BASE = "https://wireox.erp.gold";

const getOrderLineImageUrl = (order) => {
  const img = order?.Images?.[0];
  const path = img?.ImagePath;
  if (!path || typeof path !== "string") return null;
  if (path.startsWith("http")) return path;
  return `${ERP_IMAGE_BASE}${path.startsWith("/") ? "" : "/"}${path}`;
};

const formatOrderDate = (docDate) => {
  if (!docDate) return "—";
  try {
    const d = new Date(docDate);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
};

/* Same country list as Create Account and Checkout Ship to */
const PROFILE_COUNTRIES = [
  "United States",
  "Canada",
  "United Kingdom",
  "Australia",
  "Germany",
  "France",
  "Italy",
  "Spain",
  "Netherlands",
  "Belgium",
  "Switzerland",
  "Austria",
  "Sweden",
  "Norway",
  "Denmark",
  "Finland",
  "Japan",
  "South Korea",
  "China",
  "India",
  "Brazil",
  "Mexico",
  "Argentina",
  "Chile",
  "New Zealand",
  "Ireland",
  "Portugal",
  "Poland",
  "Czech Republic",
  "Hungary",
  "Romania",
  "Bulgaria",
  "Croatia",
  "Slovenia",
  "Slovakia",
  "Estonia",
  "Latvia",
  "Lithuania",
  "Greece",
  "Cyprus",
  "Malta",
  "Luxembourg",
  "Iceland",
  "Liechtenstein",
  "Monaco",
  "San Marino",
  "Vatican City",
  "Andorra",
];

const COUNTRY_CODES = [
  {
    code: "+1",
    country: "US",
    flag: "https://flagcdn.com/w20/us.png",
    display: "US +1",
  },
  {
    code: "+1",
    country: "CA",
    flag: "https://flagcdn.com/w20/ca.png",
    display: "CA +1",
  },
  {
    code: "+44",
    country: "UK",
    flag: "https://flagcdn.com/w20/gb.png",
    display: "UK +44",
  },
  {
    code: "+61",
    country: "AU",
    flag: "https://flagcdn.com/w20/au.png",
    display: "AU +61",
  },
  {
    code: "+49",
    country: "DE",
    flag: "https://flagcdn.com/w20/de.png",
    display: "DE +49",
  },
  {
    code: "+33",
    country: "FR",
    flag: "https://flagcdn.com/w20/fr.png",
    display: "FR +33",
  },
  {
    code: "+39",
    country: "IT",
    flag: "https://flagcdn.com/w20/it.png",
    display: "IT +39",
  },
  {
    code: "+34",
    country: "ES",
    flag: "https://flagcdn.com/w20/es.png",
    display: "ES +34",
  },
  {
    code: "+31",
    country: "NL",
    flag: "https://flagcdn.com/w20/nl.png",
    display: "NL +31",
  },
  {
    code: "+32",
    country: "BE",
    flag: "https://flagcdn.com/w20/be.png",
    display: "BE +32",
  },
  {
    code: "+41",
    country: "CH",
    flag: "https://flagcdn.com/w20/ch.png",
    display: "CH +41",
  },
  {
    code: "+43",
    country: "AT",
    flag: "https://flagcdn.com/w20/at.png",
    display: "AT +43",
  },
  {
    code: "+46",
    country: "SE",
    flag: "https://flagcdn.com/w20/se.png",
    display: "SE +46",
  },
  {
    code: "+47",
    country: "NO",
    flag: "https://flagcdn.com/w20/no.png",
    display: "NO +47",
  },
  {
    code: "+45",
    country: "DK",
    flag: "https://flagcdn.com/w20/dk.png",
    display: "DK +45",
  },
  {
    code: "+358",
    country: "FI",
    flag: "https://flagcdn.com/w20/fi.png",
    display: "FI +358",
  },
  {
    code: "+81",
    country: "JP",
    flag: "https://flagcdn.com/w20/jp.png",
    display: "JP +81",
  },
  {
    code: "+82",
    country: "KR",
    flag: "https://flagcdn.com/w20/kr.png",
    display: "KR +82",
  },
  {
    code: "+86",
    country: "CN",
    flag: "https://flagcdn.com/w20/cn.png",
    display: "CN +86",
  },
  {
    code: "+91",
    country: "IN",
    flag: "https://flagcdn.com/w20/in.png",
    display: "IN +91",
  },
  {
    code: "+92",
    country: "PK",
    flag: "https://flagcdn.com/w20/pk.png",
    display: "PK +92",
  },
  {
    code: "+55",
    country: "BR",
    flag: "https://flagcdn.com/w20/br.png",
    display: "BR +55",
  },
  {
    code: "+52",
    country: "MX",
    flag: "https://flagcdn.com/w20/mx.png",
    display: "MX +52",
  },
  {
    code: "+54",
    country: "AR",
    flag: "https://flagcdn.com/w20/ar.png",
    display: "AR +54",
  },
  {
    code: "+56",
    country: "CL",
    flag: "https://flagcdn.com/w20/cl.png",
    display: "CL +56",
  },
  {
    code: "+64",
    country: "NZ",
    flag: "https://flagcdn.com/w20/nz.png",
    display: "NZ +64",
  },
  {
    code: "+353",
    country: "IE",
    flag: "https://flagcdn.com/w20/ie.png",
    display: "IE +353",
  },
  {
    code: "+351",
    country: "PT",
    flag: "https://flagcdn.com/w20/pt.png",
    display: "PT +351",
  },
  {
    code: "+48",
    country: "PL",
    flag: "https://flagcdn.com/w20/pl.png",
    display: "PL +48",
  },
  {
    code: "+420",
    country: "CZ",
    flag: "https://flagcdn.com/w20/cz.png",
    display: "CZ +420",
  },
  {
    code: "+36",
    country: "HU",
    flag: "https://flagcdn.com/w20/hu.png",
    display: "HU +36",
  },
  {
    code: "+40",
    country: "RO",
    flag: "https://flagcdn.com/w20/ro.png",
    display: "RO +40",
  },
  {
    code: "+359",
    country: "BG",
    flag: "https://flagcdn.com/w20/bg.png",
    display: "BG +359",
  },
  {
    code: "+385",
    country: "HR",
    flag: "https://flagcdn.com/w20/hr.png",
    display: "HR +385",
  },
  {
    code: "+386",
    country: "SI",
    flag: "https://flagcdn.com/w20/si.png",
    display: "SI +386",
  },
  {
    code: "+421",
    country: "SK",
    flag: "https://flagcdn.com/w20/sk.png",
    display: "SK +421",
  },
  {
    code: "+372",
    country: "EE",
    flag: "https://flagcdn.com/w20/ee.png",
    display: "EE +372",
  },
  {
    code: "+371",
    country: "LV",
    flag: "https://flagcdn.com/w20/lv.png",
    display: "LV +371",
  },
  {
    code: "+370",
    country: "LT",
    flag: "https://flagcdn.com/w20/lt.png",
    display: "LT +370",
  },
  {
    code: "+30",
    country: "GR",
    flag: "https://flagcdn.com/w20/gr.png",
    display: "GR +30",
  },
  {
    code: "+357",
    country: "CY",
    flag: "https://flagcdn.com/w20/cy.png",
    display: "CY +357",
  },
  {
    code: "+356",
    country: "MT",
    flag: "https://flagcdn.com/w20/mt.png",
    display: "MT +356",
  },
  {
    code: "+352",
    country: "LU",
    flag: "https://flagcdn.com/w20/lu.png",
    display: "LU +352",
  },
  {
    code: "+354",
    country: "IS",
    flag: "https://flagcdn.com/w20/is.png",
    display: "IS +354",
  },
  {
    code: "+423",
    country: "LI",
    flag: "https://flagcdn.com/w20/li.png",
    display: "LI +423",
  },
  {
    code: "+377",
    country: "MC",
    flag: "https://flagcdn.com/w20/mc.png",
    display: "MC +377",
  },
  {
    code: "+378",
    country: "SM",
    flag: "https://flagcdn.com/w20/sm.png",
    display: "SM +378",
  },
  {
    code: "+39",
    country: "VA",
    flag: "https://flagcdn.com/w20/va.png",
    display: "VA +39",
  },
  {
    code: "+376",
    country: "AD",
    flag: "https://flagcdn.com/w20/ad.png",
    display: "AD +376",
  },
];

const mapProfileToForm = (profile) => {
  if (!profile) return null;
  return {
    firstName: profile.FirstName ?? "",
    lastName: profile.LastName ?? "",
    userName: profile.UserName ?? "",
    email: profile.Email ?? "",
    countryCode: "+1",
    mobile: profile.PhoneNumber ?? "",
    streetAddress: profile.BusinessStreetAddress ?? "",
    suite: profile.BusinessSuite ?? "",
    city: profile.BusinessCity ?? "",
    state: profile.BusinessState ?? "",
    zipCode: profile.BusinessZipCode ?? "",
    country: profile.BusinessCountry ?? profile.Country ?? "United States",
    legalEntityName: profile.LegalEntityName ?? "",
    businessName: profile.BusinessName ?? "",
    businessPhoneNumber: profile.BusinessPhoneNumber ?? "",
    businessWebsite: profile.BusinessWesbite ?? profile.BusinessWebsite ?? "",
    ein: profile.EIN ?? "",
  };
};

const MyAccount = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("profile");
  const [isEditing, setIsEditing] = useState(false);
  const [isSecurityEditing, setIsSecurityEditing] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState(null);
  const [profileSaveLoading, setProfileSaveLoading] = useState(false);
  const [profileModalMessage, setProfileModalMessage] = useState("");
  const [securityLoading, setSecurityLoading] = useState(false);
  const [securityError, setSecurityError] = useState(null);
  const [resetPasswordData, setResetPasswordData] = useState(null);
  const [passwordMatchError, setPasswordMatchError] = useState("");
  const [securityModalMessage, setSecurityModalMessage] = useState("");
  const [securitySaveLoading, setSecuritySaveLoading] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("visa");
  const [isPaymentEditing, setIsPaymentEditing] = useState(false);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [billingModalForm, setBillingModalForm] = useState({
    cardNumber: "",
    expirationDate: "",
    securityCode: "",
    firstName: "",
    lastName: "",
    rememberCard: false,
    billingCountry: "United States",
    streetAddress: "",
    streetAddress2: "",
    city: "",
    state: "",
    zipCode: "",
  });
  const [addCardErrors, setAddCardErrors] = useState({});
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState(null);
  const [ordersList, setOrdersList] = useState([]);
  const [ordersPagination, setOrdersPagination] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    userName: "",
    email: "",
    countryCode: "+1",
    mobile: "",
    streetAddress: "",
    suite: "",
    city: "",
    state: "",
    zipCode: "",
    country: "United States",
    legalEntityName: "",
    businessName: "",
    businessPhoneNumber: "",
    businessWebsite: "",
    ein: "",
  });

  // If not logged in, redirect to login
  useEffect(() => {
    if (!isUserLoggedIn()) {
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  // Fetch user profile when logged in (UserName = logged-in user's email)
  useEffect(() => {
    if (!isUserLoggedIn()) return;
    const userData = getUserData();
    const userName = userData?.email;
    if (!userName) {
      setProfileLoading(false);
      return;
    }
    let cancelled = false;
    setProfileLoading(true);
    setProfileError(null);
    userProfileService
      .getUserProfile(userName)
      .then((profile) => {
        if (cancelled) return;
        const mapped = mapProfileToForm(profile);
        if (mapped) setFormData(mapped);
      })
      .catch((err) => {
        if (cancelled) return;
        setProfileError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load profile.",
        );
      })
      .finally(() => {
        if (!cancelled) setProfileLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // When user clicks Security, call reset password API to get token for later update password
  useEffect(() => {
    if (activeSection !== "security" || !isUserLoggedIn()) return;
    const userData = getUserData();
    const userName = userData?.email;
    if (!userName) return;
    setSecurityLoading(true);
    setSecurityError(null);
    userProfileService
      .getResetPasswordToken(userName)
      .then((data) => {
        setResetPasswordData(data);
      })
      .catch((err) => {
        setSecurityError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load security settings.",
        );
      })
      .finally(() => {
        setSecurityLoading(false);
      });
  }, [activeSection]);

  // Order history — GET order/list (6 per page)
  useEffect(() => {
    if (activeSection !== "orders" || !isUserLoggedIn()) return;
    const userData = getUserData();
    const customerId = userData?.customerId;
    const userName = userData?.email;
    if (customerId == null || customerId === "" || !userName) {
      setOrdersError("Unable to load orders: missing customer account.");
      setOrdersList([]);
      setOrdersPagination(null);
      return;
    }
    setOrdersLoading(true);
    setOrdersError(null);
    orderService
      .getOrderList(customerId, ordersPage, ORDER_PAGE_SIZE, userName)
      .then((data) => {
        setOrdersList(Array.isArray(data?.Result) ? data.Result : []);
        setOrdersPagination(data?.Pagination ?? null);
      })
      .catch((err) => {
        setOrdersList([]);
        setOrdersPagination(null);
        setOrdersError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load order history.",
        );
      })
      .finally(() => {
        setOrdersLoading(false);
      });
  }, [activeSection, ordersPage]);

  const buildCartItemFromOrder = (order) => {
    const itemId = order.ItemCodeID ?? order.ItemCodeId;
    const imgSrc = getOrderLineImageUrl(order) || defaultProductImage;
    return {
      id: itemId,
      name: order.ItemName || "Item",
      price: Number(order.TotalLineItemPrice ?? order.SubTotal ?? 0),
      quantity: 1,
      image: imgSrc,
    };
  };

  const handleBuyAgainFromOrder = (order) => {
    const line = buildCartItemFromOrder(order);
    if (line.id == null) return;
    saveCartItems([line]);
    setSelectedOrder(null);
    navigate("/checkout");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const buildUserProfilePayload = (data, customerId) => ({
    CustomerID: Number(customerId) || customerId,
    FirstName: (data.firstName || "").trim(),
    LastName: (data.lastName || "").trim(),
    Country: data.country || "United States",
    UserName: null,
    Email: (data.email || "").trim(),
    PhoneNumber: String(data.mobile || "").replace(/\D/g, ""),
    LegalEntityName: (data.legalEntityName || "").trim(),
    BusinessName: (data.businessName || "").trim(),
    BusinessCategory: 0,
    BusinessStreetAddress: (data.streetAddress || "").trim(),
    BusinessSuite: (data.suite || "").trim() || null,
    BusinessCity: (data.city || "").trim(),
    BusinessZipCode: (data.zipCode || "").trim(),
    BusinessState: (data.state || "").trim(),
    BusinessCountry: data.country || "United States",
    BusinessPhoneNumber: String(data.businessPhoneNumber || "").replace(
      /\D/g,
      "",
    ),
    BusinessWesbite: (data.businessWebsite || "").trim(),
    EIN: (data.ein || "").trim(),
    PreferrredLanguage: null,
    PreferrredCurrency: null,
  });

  const handleSave = () => {
    const userData = getUserData();
    const userName = userData?.email;
    const customerId = userData?.customerId;
    if (!userName) {
      setProfileModalMessage("You must be logged in to save your profile.");
      return;
    }
    if (customerId == null || customerId === "") {
      setProfileModalMessage("Missing customer account. Please log in again.");
      return;
    }
    setProfileSaveLoading(true);
    setProfileModalMessage("");
    const payload = buildUserProfilePayload(formData, customerId);
    userProfileService
      .updateUserProfile(payload, userName)
      .then((res) => {
        const mapped = mapProfileToForm(res);
        if (mapped) setFormData(mapped);
        setProfileModalMessage(
          res?.msg ?? res?.message ?? "Profile saved successfully.",
        );
        setIsEditing(false);
      })
      .catch((err) => {
        setProfileModalMessage(
          err?.response?.data?.message ??
            err?.response?.data?.msg ??
            err?.message ??
            "Failed to save profile.",
        );
      })
      .finally(() => {
        setProfileSaveLoading(false);
      });
  };

  const closeProfileModal = () => {
    setProfileModalMessage("");
  };

  const handleSecurityCancel = () => {
    setIsSecurityEditing(false);
    setNewPassword("");
    setConfirmPassword("");
    setPasswordMatchError("");
  };

  const handleSecuritySave = () => {
    if (newPassword !== confirmPassword) {
      setPasswordMatchError("Password and Confirm Password must match.");
      return;
    }
    setPasswordMatchError("");
    if (
      !resetPasswordData?.UserID ||
      !resetPasswordData?.Token ||
      !resetPasswordData?.UserName
    ) {
      setSecurityModalMessage(
        "Security session expired. Please switch away and back to Security to try again.",
      );
      return;
    }
    const userData = getUserData();
    const userName = userData?.email;
    if (!userName) {
      setSecurityModalMessage("You must be logged in to change password.");
      return;
    }
    setSecuritySaveLoading(true);
    const payload = {
      UserID: resetPasswordData.UserID,
      UserName: resetPasswordData.UserName,
      Token: resetPasswordData.Token,
      NewPassword: newPassword,
      ConfirmNewPassword: confirmPassword,
    };
    userProfileService
      .resetPassword(payload, userName)
      .then((res) => {
        const message =
          res?.msg ??
          res?.message ??
          (res?.success ? "Password Change Successfully" : "Done.");
        setSecurityModalMessage(message);
        setIsSecurityEditing(false);
        setNewPassword("");
        setConfirmPassword("");
      })
      .catch((err) => {
        const message =
          err?.response?.data?.msg ??
          err?.response?.data?.message ??
          err?.message ??
          "Failed to update password.";
        setSecurityModalMessage(message);
      })
      .finally(() => {
        setSecuritySaveLoading(false);
      });
  };

  const closeSecurityModal = () => {
    setSecurityModalMessage("");
  };

  const handlePaymentCancel = () => {
    setIsPaymentEditing(false);
  };

  const handlePaymentSave = () => {
    setIsPaymentEditing(false);
  };

  const openAddCardModal = () => {
    setBillingModalForm({
      cardNumber: "",
      expirationDate: "",
      securityCode: "",
      firstName: formData.firstName || "",
      lastName: formData.lastName || "",
      rememberCard: false,
      billingCountry: formData.country || "United States",
      streetAddress: formData.streetAddress || "",
      streetAddress2: formData.suite || "",
      city: formData.city || "",
      state: formData.state || "",
      zipCode: formData.zipCode || "",
    });
    setAddCardErrors({});
    setShowAddCardModal(true);
  };

  const closeAddCardModal = () => {
    setShowAddCardModal(false);
    setAddCardErrors({});
  };

  const handleAddCardInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBillingModalForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (addCardErrors[name]) {
      setAddCardErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleAddCardSubmit = (e) => {
    e.preventDefault();
    const err = {};
    if (!billingModalForm.cardNumber?.trim()) err.cardNumber = true;
    if (!billingModalForm.streetAddress?.trim()) err.streetAddress = true;
    setAddCardErrors(err);
    if (Object.keys(err).length > 0) return;
    closeAddCardModal();
  };

  const PAYMENT_OPTIONS = [
    { id: "visa", label: "Visa" },
    { id: "mastercard", label: "Master Card" },
  ];

  const totalOrders = ordersPagination?.TotalCount ?? 0;
  const totalOrderPages =
    totalOrders > 0 ? Math.ceil(totalOrders / ORDER_PAGE_SIZE) : 1;
  const rangeStart =
    totalOrders === 0 ? 0 : (ordersPage - 1) * ORDER_PAGE_SIZE + 1;
  const rangeEnd = Math.min(ordersPage * ORDER_PAGE_SIZE, totalOrders);

  const contentAreaLoading =
    (activeSection === "profile" && (profileLoading || profileSaveLoading)) ||
    (activeSection === "security" && securityLoading) ||
    (activeSection === "orders" && ordersLoading);

  return (
    <div className="ma-my-account-page">
      {selectedOrder && (
        <OrderHistoryDetailModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onBuyAgain={handleBuyAgainFromOrder}
        />
      )}
      {/* Security / Profile result modal (same style as Create Account success popup) */}
      {(securityModalMessage || profileModalMessage) && (
        <div
          className="ma-result-popup"
          onClick={() => {
            closeSecurityModal();
            closeProfileModal();
          }}
        >
          <div
            className="ma-result-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="ma-result-popup-close"
              onClick={() => {
                closeSecurityModal();
                closeProfileModal();
              }}
              aria-label="Close"
            >
              ×
            </button>
            <p className="ma-result-popup-message">
              {securityModalMessage || profileModalMessage}
            </p>
          </div>
        </div>
      )}

      {/* Add New Card modal – same as checkout "Credit or Debit Card" */}
      <BillingCardModal
        isOpen={showAddCardModal}
        onClose={closeAddCardModal}
        formData={billingModalForm}
        onFormChange={handleAddCardInputChange}
        onSubmit={handleAddCardSubmit}
        errors={addCardErrors}
        showCardFields
      />
      <h1 className="ma-page-title">
        {activeSection === "profile"
          ? "My Account"
          : (SECTIONS.find((s) => s.id === activeSection)?.label ??
            "My Account")}
      </h1>

      <div className="ma-layout">
        {/* Left sidebar – nav buttons */}
        <nav className="ma-sidebar">
          <ul className="ma-nav-list">
            {SECTIONS.map((section) => (
              <li key={section.id} className="ma-nav-item">
                <button
                  type="button"
                  className={`ma-nav-btn ${activeSection === section.id ? "ma-nav-btn--active" : ""}`}
                  onClick={() => setActiveSection(section.id)}
                >
                  {section.label}
                </button>
              </li>
            ))}
          </ul>
          <div className="ma-sidebar-logout">
            <button
              type="button"
              className="ma-nav-btn ma-logout-btn-sidebar"
              onClick={() => {
                clearCart();
                logout();
                navigate("/", { replace: true });
              }}
            >
              Logout
            </button>
          </div>
        </nav>

        {/* Right content area */}
        <div
          className={`ma-content${contentAreaLoading ? " ma-content--loading" : ""}`}
          aria-busy={contentAreaLoading}
        >
          {contentAreaLoading && (
            <div
              className="ma-content-loader-overlay"
              role="status"
              aria-live="polite"
              aria-label="Loading"
            >
              <div className="ma-content-spinner" aria-hidden />
              <span className="ma-content-loader-label">Loading…</span>
            </div>
          )}
          {activeSection === "profile" && (
            <div className="ma-profile-section">
              <div className="ma-profile-header">
                <h2 className="ma-content-title">My Profile</h2>
                <button
                  type="button"
                  className="ma-edit-btn"
                  onClick={() => setIsEditing(!isEditing)}
                  disabled={profileLoading || profileSaveLoading}
                >
                  Edit
                </button>
              </div>
              {profileError && (
                <p className="ma-profile-error">{profileError}</p>
              )}
              <div className="ma-profile-form">
                <div className="ma-form-row ma-form-row--two">
                  <div className="ma-field">
                    <label className="ma-field-label">First Name</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder="Your First Name"
                      className="ma-input"
                      disabled={!isEditing}
                    />
                  </div>
                  <div className="ma-field">
                    <label className="ma-field-label">Last Name</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder="Last Name"
                      className="ma-input"
                      disabled={!isEditing}
                    />
                  </div>
                </div>
                {/* <div className="ma-form-row">
                  <div className="ma-field">
                    <label className="ma-field-label">User Name</label>
                    <input
                      type="text"
                      name="userName"
                      value={formData.userName}
                      onChange={handleInputChange}
                      placeholder="Enter User Name"
                      className="ma-input"
                      disabled={!isEditing}
                    />
                  </div>
                </div> */}
                <div className="ma-form-row ma-form-row--two">
                  <div className="ma-field">
                    <label
                      className="ma-field-label"
                      htmlFor="ma-profile-username"
                    >
                      Username
                    </label>
                    <input
                      id="ma-profile-username"
                      type="text"
                      name="email"
                      value={formData.email}
                      readOnly
                      disabled={!isEditing}
                      autoComplete="username"
                      placeholder="Username"
                      className="ma-input"
                      aria-readonly="true"
                    />
                  </div>

                  <div className="ma-field">
                    <label className="ma-field-label">Mobile Number</label>

                    <div className="ma-mobile-container">
                      <div
                        className={`ma-country-code-selector ${!isEditing ? "ma-country-code-selector--disabled" : ""}`}
                      >
                        <div className="ma-country-code-display">
                          <img
                            src={
                              COUNTRY_CODES.find(
                                (item) => item.code === formData.countryCode,
                              )?.flag || COUNTRY_CODES[0].flag
                            }
                            alt="flag"
                            className="ma-flag-image"
                          />
                          <span className="ma-country-code-text">
                            {formData.countryCode}
                          </span>
                          <span className="ma-dropdown-arrow">▼</span>
                        </div>
                        <select
                          name="countryCode"
                          value={formData.countryCode}
                          onChange={handleInputChange}
                          className="ma-country-code-select"
                          disabled={!isEditing}
                        >
                          {COUNTRY_CODES.map((item) => (
                            <option
                              key={`${item.code}-${item.country}`}
                              value={item.code}
                            >
                              {item.display}
                            </option>
                          ))}
                        </select>
                      </div>
                      <input
                        type="tel"
                        name="mobile"
                        value={formData.mobile}
                        onChange={handleInputChange}
                        placeholder="123-456-789"
                        className="ma-input ma-mobile-input"
                        disabled={!isEditing}
                      />
                    </div>
                  </div>
                </div>
                <div className="ma-form-row ma-form-row--two">
                  <div className="ma-field">
                    <label className="ma-field-label">
                      Suite / Unit / Floor
                    </label>
                    <input
                      type="text"
                      name="suite"
                      value={formData.suite}
                      onChange={handleInputChange}
                      placeholder="Enter your suite, unit, etc."
                      className="ma-input"
                      disabled={!isEditing}
                    />
                  </div>
                  <div className="ma-field">
                    <label className="ma-field-label">Street Address</label>
                    <input
                      type="text"
                      name="streetAddress"
                      value={formData.streetAddress}
                      onChange={handleInputChange}
                      placeholder="Enter Your Street Address"
                      className="ma-input"
                      disabled={!isEditing}
                    />
                  </div>
                </div>
                <div className="ma-form-row ma-form-row--two">
                  <div className="ma-field">
                    <label className="ma-field-label">State</label>
                    <select
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      className="ma-input"
                      disabled={!isEditing}
                    >
                      <option value="">Select Your State/Province</option>
                      <option value="AL">Alabama</option>
                      <option value="CA">California</option>
                      <option value="MI">Michigan</option>
                      <option value="NY">New York</option>
                      <option value="TX">Texas</option>
                    </select>
                  </div>
                  <div className="ma-field">
                    <label className="ma-field-label">City</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder="Enter Your City"
                      className="ma-input"
                      disabled={!isEditing}
                    />
                  </div>
                </div>
                <div className="ma-form-row ma-form-row--two">
                  <div className="ma-field">
                    <label className="ma-field-label">Country</label>
                    <select
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="ma-input"
                    >
                      {PROFILE_COUNTRIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="ma-field">
                    <label className="ma-field-label">ZIP Code</label>
                    <input
                      type="text"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleInputChange}
                      placeholder="Enter your ZIP Code"
                      className="ma-input"
                      disabled={!isEditing}
                    />
                  </div>
                </div>
                <div className="ma-form-row ma-form-row--two">
                  <div className="ma-field">
                    <label className="ma-field-label">Business Name</label>
                    <input
                      type="text"
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleInputChange}
                      placeholder="Business Name"
                      className="ma-input"
                      disabled={!isEditing}
                    />
                  </div>
                  <div className="ma-field">
                    <label className="ma-field-label">Legal Entity Name</label>
                    <input
                      type="text"
                      name="legalEntityName"
                      value={formData.legalEntityName}
                      onChange={handleInputChange}
                      placeholder="Legal Entity Name"
                      className="ma-input"
                      disabled={!isEditing}
                    />
                  </div>
                </div>
                <div className="ma-form-row ma-form-row--two">
                  <div className="ma-field">
                    <label className="ma-field-label">Business Website</label>
                    <input
                      type="url"
                      name="businessWebsite"
                      value={formData.businessWebsite}
                      onChange={handleInputChange}
                      placeholder="https://"
                      className="ma-input"
                      disabled={!isEditing}
                    />
                  </div>
                  <div className="ma-field">
                    <label className="ma-field-label">
                      Business Phone Number
                    </label>
                    <input
                      type="tel"
                      name="businessPhoneNumber"
                      value={formData.businessPhoneNumber}
                      onChange={handleInputChange}
                      placeholder="Business Phone"
                      className="ma-input"
                      disabled={!isEditing}
                    />
                  </div>
                </div>
                <div className="ma-form-row">
                  <div className="ma-field">
                    <label className="ma-field-label">
                      EIN / Federal Tax ID
                    </label>
                    <input
                      type="text"
                      name="ein"
                      value={formData.ein}
                      onChange={handleInputChange}
                      placeholder="EIN"
                      className="ma-input"
                      disabled={!isEditing}
                    />
                  </div>
                </div>
                <div className="ma-form-actions">
                  <button
                    type="button"
                    className="ma-btn ma-btn--cancel"
                    onClick={handleCancel}
                    disabled={!isEditing || profileSaveLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="ma-btn ma-btn--save"
                    onClick={handleSave}
                    disabled={!isEditing || profileSaveLoading}
                  >
                    {profileSaveLoading ? "Saving…" : "Save Changes"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeSection === "security" && (
            <div className="ma-security-section">
              {securityError && (
                <p className="ma-profile-error">{securityError}</p>
              )}
              <div className="ma-security-header">
                <h2 className="ma-security-title">YOUR PASSWORD</h2>
                <button
                  type="button"
                  className="ma-edit-btn"
                  onClick={() => setIsSecurityEditing(!isSecurityEditing)}
                  disabled={securityLoading}
                >
                  Edit
                </button>
              </div>
              <p className="ma-security-description">
                Choose a strong password to secure your account. Make sure
                it&apos;s easy for you to remember but hard for others to guess.
              </p>
              <div className="ma-security-form">
                <div className="ma-form-row">
                  <div className="ma-field">
                    <label className="ma-field-label">New Password</label>
                    <div className="ma-password-wrap">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => {
                          setNewPassword(e.target.value);
                          setPasswordMatchError("");
                        }}
                        placeholder="Enter Password"
                        className="ma-input"
                        disabled={!isSecurityEditing}
                      />
                      <button
                        type="button"
                        className="ma-password-toggle"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        disabled={!isSecurityEditing}
                        aria-label={
                          showNewPassword ? "Hide password" : "Show password"
                        }
                      >
                        {showNewPassword ? (
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                            <line x1="1" y1="1" x2="23" y2="23" />
                          </svg>
                        ) : (
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="ma-form-row">
                  <div className="ma-field">
                    <label className="ma-field-label">Confirm Password</label>
                    <div className="ma-password-wrap">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          setPasswordMatchError("");
                        }}
                        placeholder="Enter Password"
                        className="ma-input"
                        disabled={!isSecurityEditing}
                      />
                      <button
                        type="button"
                        className="ma-password-toggle"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        disabled={!isSecurityEditing}
                        aria-label={
                          showConfirmPassword
                            ? "Hide password"
                            : "Show password"
                        }
                      >
                        {showConfirmPassword ? (
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                            <line x1="1" y1="1" x2="23" y2="23" />
                          </svg>
                        ) : (
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        )}
                      </button>
                    </div>
                    {passwordMatchError && (
                      <p className="ma-field-error">{passwordMatchError}</p>
                    )}
                  </div>
                </div>
                <div className="ma-form-actions">
                  <button
                    type="button"
                    className="ma-btn ma-btn--cancel"
                    onClick={handleSecurityCancel}
                    disabled={!isSecurityEditing}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="ma-btn ma-btn--save"
                    onClick={handleSecuritySave}
                    disabled={!isSecurityEditing || securitySaveLoading}
                  >
                    {securitySaveLoading ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* {activeSection === "payments" && (
            <div className="ma-payments-section">
              <div className="ma-payments-header">
                <h2 className="ma-content-title">Payment</h2>
                <button
                  type="button"
                  className="ma-edit-btn"
                  onClick={openAddCardModal}
                >
                  Add New Card
                </button>
              </div>
              <div className="ma-payments-form">
                <div className="ma-payment-options">
                  {PAYMENT_OPTIONS.map((option) => (
                    <label
                      key={option.id}
                      className={`ma-payment-option ${selectedPaymentMethod === option.id ? "ma-payment-option--selected" : ""}`}
                    >
                      <input
                        type="radio"
                        name="paymentMethod"
                        value={option.id}
                        checked={selectedPaymentMethod === option.id}
                        onChange={() => setSelectedPaymentMethod(option.id)}
                        disabled={!isPaymentEditing}
                        className="ma-payment-radio"
                      />
                      <span
                        className={`ma-payment-logo-box ma-payment-logo-box--${option.id}`}
                      >
                        {option.id === "visa" ? "VISA" : "Master Card"}
                      </span>
                      <span className="ma-payment-label">{option.label}</span>
                    </label>
                  ))}
                </div>
                <div className="ma-form-actions">
                  <button
                    type="button"
                    className="ma-btn ma-btn--cancel"
                    onClick={handlePaymentCancel}
                    disabled={!isPaymentEditing}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="ma-btn ma-btn--save"
                    onClick={handlePaymentSave}
                    disabled={!isPaymentEditing}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          )} */}

          {activeSection === "orders" && (
            <div className="ma-order-history-section">
              <h2 className="ma-content-title">Order History</h2>
              {ordersError && !ordersLoading && (
                <p className="ma-profile-error">{ordersError}</p>
              )}
              {!ordersLoading && !ordersError && ordersList.length === 0 && (
                <p className="ma-order-empty">No orders yet.</p>
              )}
              {ordersList.length > 0 && (
                <>
                  <div className="ma-order-range" aria-live="polite">
                    {totalOrders > 0
                      ? `Showing ${rangeStart}–${rangeEnd} of ${totalOrders} orders`
                      : ""}
                  </div>
                  <ul className="ma-order-history-list">
                    {ordersList.map((order) => {
                      const imgSrc =
                        getOrderLineImageUrl(order) || defaultProductImage;
                      const itemId = order.ItemCodeID ?? order.ItemCodeId;
                      return (
                        <li
                          key={order.ID}
                          className="ma-order-history-item ma-order-history-item--clickable"
                          role="button"
                          tabIndex={0}
                          onClick={() => setSelectedOrder(order)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setSelectedOrder(order);
                            }
                          }}
                        >
                          <div className="ma-order-item-image-wrap">
                            <img
                              src={imgSrc}
                              alt=""
                              className="ma-order-item-image"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = defaultProductImage;
                              }}
                            />
                          </div>
                          <div className="ma-order-item-body">
                            <div className="ma-order-item-info">
                              <span className="ma-order-item-txn">
                                {order.TxnNumber || "—"}
                              </span>
                              <span className="ma-order-item-date">
                                {formatOrderDate(order.DocDate)}
                              </span>
                            </div>
                            <div className="ma-order-item-actions">
                              <span className="ma-order-item-total">
                                Total: $
                                {(() => {
                                  const total =
                                    Number(
                                      order.TotalAmount ??
                                        order.TotalPrice ??
                                        order.TotalLineItemPrice ??
                                        order.SubTotal ??
                                        0,
                                    ) || 0;
                                  return total.toFixed(2);
                                })()}
                              </span>
                              {itemId != null && (
                                <button
                                  type="button"
                                  className="ma-order-item-buy-again"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleBuyAgainFromOrder(order);
                                  }}
                                >
                                  Buy Again
                                </button>
                              )}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  {totalOrderPages > 1 && (
                    <nav
                      className="ma-order-pagination"
                      aria-label="Order list pages"
                    >
                      <button
                        type="button"
                        className="ma-order-page-btn"
                        disabled={ordersPage <= 1 || ordersLoading}
                        onClick={() => setOrdersPage((p) => Math.max(1, p - 1))}
                      >
                        Previous
                      </button>
                      <span className="ma-order-page-info">
                        Page {ordersPage} of {totalOrderPages}
                      </span>
                      <button
                        type="button"
                        className="ma-order-page-btn"
                        disabled={
                          ordersPage >= totalOrderPages || ordersLoading
                        }
                        onClick={() =>
                          setOrdersPage((p) => Math.min(totalOrderPages, p + 1))
                        }
                      >
                        Next
                      </button>
                    </nav>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyAccount;
